import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PaymentBadge } from "@/components/PaymentStatus";
import type { PaymentInfo } from "@/lib/api";

/** One finalized billing line item (backend-computed; the UI only formats). */
interface LineItem {
  start_at?: string | null;
  end_at?: string | null;
  fee_type: string;
  uom: string;
  rate: number;             // dollars per uom
  amount_cents: number;
  pricing_reason?: string;
  billable?: boolean;
  energy_kwh?: number;
  hours?: number;
  approximation?: string;
}

export interface HistorySession {
  session_id: string;
  start_time?: string | null;
  end_time?: string | null;
  duration_seconds?: number;
  charging_seconds?: number;
  energy_delivered?: number; // Wh
  customer_name?: string | null;
  customer_email?: string | null;
  authorized_by?: string;
  ended_by?: string | null;
  pricing_profile_name?: string | null;
  pricing_source?: string | null;
  billing_source?: string;
  costs?: {
    total_cost?: number | null;
    currency_code?: string;
    line_items?: LineItem[];
  };
  // Payment status + collected-vs-billed, projected the SAME way as the admin org
  // sessions report (attached by GET /api/unit/<id>/sessions). Absent on legacy /
  // dev-memory sessions — the dialog simply omits the Payment section then.
  payment?: PaymentInfo;
  // Client-only: set when the payment projection failed to load for this row's
  // page (response payment_available === false) so the dialog can show
  // "Unavailable" instead of silently omitting the amounts.
  paymentUnavailable?: boolean;
}

function money(dollars: number, currency = "USD"): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(dollars);
}

function fmtDateTime(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function fmtTime(iso?: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function fmtDuration(seconds?: number | null): string {
  if (seconds == null || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const FEE_LABEL: Record<string, string> = {
  "charging|kwh": "Energy",
  "charging|hour": "Charging time",
  "idle|hour": "Idle",
  "activation|session": "Activation",
};

function feeLabel(li: LineItem): string {
  return FEE_LABEL[`${li.fee_type}|${li.uom}`] ?? li.fee_type;
}

function qtyText(li: LineItem): string {
  if (li.energy_kwh != null) return `${li.energy_kwh.toFixed(3)} kWh`;
  if (li.hours != null) return fmtDuration(li.hours * 3600);
  return "";
}

const SOURCE_LABEL: Record<string, string> = {
  pricing_profile: "Pricing profile",
  unit_default: "Charger default tariff",
};

const AUTHORIZED_LABEL: Record<string, string> = {
  admin: "Admin",
  customer: "Customer",
  auto: "Auto-start",
};

/**
 * Read-only detail dialog for one completed session from the device-page
 * history. Renders the finalized per-interval billing line items (paid AND
 * free segments) — no tariff math happens here.
 */
export function HistorySessionDetail({ session, showCosts, onClose }: {
  session: HistorySession | null;
  showCosts: boolean;
  onClose: () => void;
}) {
  const s = session;
  const items = s?.costs?.line_items ?? [];
  const currency = s?.costs?.currency_code ?? "USD";
  const totalCost = s?.costs?.total_cost;
  const notFinalized = s?.billing_source === "pending" || s?.billing_source === "failed";

  return (
    <Dialog open={!!s} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        {s && (
          <>
            <DialogHeader>
              <DialogTitle>Session Details</DialogTitle>
              <DialogDescription className="font-mono text-xs">{s.session_id}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Timing + energy overview */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <DetailItem label="Session start" value={fmtDateTime(s.start_time)} />
                <DetailItem label="Session end" value={fmtDateTime(s.end_time)} />
                <DetailItem label="Plugged-in time" value={fmtDuration(s.duration_seconds)} />
                <DetailItem label="Charging time" value={fmtDuration(s.charging_seconds)} />
                <DetailItem label="Energy delivered" value={s.energy_delivered != null ? `${(s.energy_delivered / 1000).toFixed(2)} kWh` : "—"} />
                {showCosts && (
                  <DetailItem
                    label="Billed"
                    value={totalCost != null ? money(totalCost, currency) : notFinalized ? "Not finalized" : "—"}
                  />
                )}
                {showCosts && (s.payment || s.paymentUnavailable) && (
                  <DetailItem
                    label="Captured"
                    value={
                      s.payment
                        ? (s.payment.captured_payment != null
                            ? money(s.payment.captured_payment, currency)
                            : "—")
                        : "Unavailable"
                    }
                  />
                )}
                {showCosts && (s.payment || s.paymentUnavailable) && (
                  <div>
                    <div className="text-xs opacity-60">Payment</div>
                    <div className="mt-0.5">
                      {s.payment ? (
                        <PaymentBadge
                          state={s.payment.payment_state}
                          needsAttention={s.payment.payment_needs_attention}
                        />
                      ) : (
                        <span className="text-xs opacity-60">Unavailable</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Who started it + which tariff applied */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <DetailItem
                  label="Started by"
                  value={s.customer_name || AUTHORIZED_LABEL[s.authorized_by ?? ""] || s.authorized_by || "—"}
                  sub={s.customer_email ?? undefined}
                />
                <DetailItem
                  label="Tariff"
                  value={s.pricing_profile_name
                    || SOURCE_LABEL[s.pricing_source ?? ""]
                    || (s.pricing_source ?? "—")}
                  sub={s.pricing_profile_name ? SOURCE_LABEL[s.pricing_source ?? ""] : undefined}
                />
              </div>

              {/* Per-interval rate breakdown — paid AND free segments */}
              {showCosts && (
                <>
                  <Separator />
                  <div>
                    <div className="text-xs font-semibold uppercase opacity-70 mb-2">Rate intervals</div>
                    {items.length > 0 ? (
                      <div className="space-y-2" data-testid="session-line-items">
                        {items.map((li, i) => (
                          <div key={i} className="flex items-start justify-between gap-3 text-sm">
                            <div>
                              <div className="flex items-center gap-2">
                                <span>{feeLabel(li)}</span>
                                {li.rate === 0 ? (
                                  <Badge variant="success">Free</Badge>
                                ) : li.billable === false ? (
                                  <Badge variant="secondary">Not charged</Badge>
                                ) : null}
                              </div>
                              <div className="text-xs opacity-60">
                                {li.start_at && li.end_at ? `${fmtTime(li.start_at)} – ${fmtTime(li.end_at)}` : null}
                                {qtyText(li) ? ` · ${qtyText(li)}` : null}
                                {li.rate > 0 ? ` × ${money(li.rate, currency)}/${li.uom === "kwh" ? "kWh" : li.uom}` : null}
                              </div>
                            </div>
                            <div className="tabular-nums font-medium shrink-0">
                              {money(li.amount_cents / 100, currency)}
                            </div>
                          </div>
                        ))}
                        {totalCost != null && (
                          <div className="flex items-center justify-between border-t border-border pt-2 text-sm font-semibold">
                            <span>Total</span>
                            <span className="tabular-nums">{money(totalCost, currency)}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm opacity-60">
                        {notFinalized
                          ? "Billing is not finalized for this session yet."
                          : "No billing breakdown available for this session."}
                      </div>
                    )}
                  </div>
                </>
              )}

              {s.ended_by && (
                <div className="text-xs opacity-60">Ended by: {s.ended_by}</div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailItem({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-xs opacity-60">{label}</div>
      <div className="font-semibold">{value}</div>
      {sub && <div className="text-xs opacity-60">{sub}</div>}
    </div>
  );
}
